import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';
import {
  categoryListGet,
  categoryDetailsGet,
  categoryCreatePost,
  categoryUpdatePut,
  categoryDeleteDel,
  categoryStatusPut,
  categoryExportGet,
  syncWithPmsPost
} from '../Model/CategoryModel';

/**
 * Get list of categories with pagination
 * @param {Object} filters - Pagination and search filters
 * @returns {Promise} Processed categories list
 */
export const getCategoryList = async (filters = {}, from = "") => {
  let queryParams = {};

  if (filters) {
    // Handle search term
    if (filters.search) {
      queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
      queryParams.filters = JSON.stringify({
        limit: filters.limit || 10,
        offset: filters.offset || 0,
        search_attr: filters.search,
        store_id: localStorage.getItem('store_id'),
        ...filters
      });
    } else {
      let filtersObj = {
        store_id: localStorage.getItem('store_id'),
        ...filters
      };
      if (from !== "product") {
        filtersObj.limit = filters.limit || 10;
        filtersObj.offset = filters.offset || 0;
      }

      // Add sorting if provided
      if (filters.sort && filters.sort.length > 0) {
        filtersObj.sort = filters.sort.map(item => ({
          colId: item.field,
          sort: item.sort
        }));
      }
      queryParams.filters = JSON.stringify(filtersObj);
    }
  }
  const response = await get(`${process.env.EXTENDED_URL}/category`, queryParams);
  return categoryListGet(response);
};

/**
 * Get single category details by ID or slug
 * @param {string|number} id - Category ID or slug
 * @returns {Promise} Processed category details
 */
export const getCategoryDetails = async (slack) => {
  const response = await get(`${process.env.EXTENDED_URL}/category/${slack}`);
  return categoryDetailsGet(response);
};

/**
 * Create a new category
 * @param {Object} categoryData - Category data to create
 * @returns {Promise} Creation response
 */
export const createCategory = async (categoryData) => {
  const apiData = {
    category_name: categoryData.name,
    description: categoryData.description || '',
    display_on_pos_screen: categoryData.display_on_pos_screen || '1',
    display_on_qr_menu: categoryData.display_on_qr_menu || '1',
    status: categoryData.status || '1',
    store_id: localStorage.getItem('store_id') || "",
    category_type: categoryData.category_type || 'food',
    kitchen_display_id: categoryData.kitchen_display_id ?? null,
    ticket_type: categoryData.ticket_type || 'KOT',
    printer_id: categoryData.printer_id ?? null
  };
  const response = await post(`${process.env.EXTENDED_URL}/category`, apiData);
  return categoryCreatePost(response);
};

/**
 * Update an existing category
 * @param {string|number} id - Category ID or slug
 * @param {Object} categoryData - Category data to update
 * @returns {Promise} Update response
 */
export const updateCategory = async (slack, categoryData) => {
  const apiData = {
    category_name: categoryData.name,
    description: categoryData.description || '',
    display_on_pos_screen: categoryData.display_on_pos_screen || '1',
    display_on_qr_menu: categoryData.display_on_qr_menu || '1',
    status: categoryData.status || '1',
    category_type: categoryData.category_type || 'food',
    kitchen_display_id: categoryData.kitchen_display_id ?? null,
    ticket_type: categoryData.ticket_type || 'KOT',
    printer_id: categoryData.printer_id ?? null
  };
  const response = await put(`${process.env.EXTENDED_URL}/category/${slack}`, apiData);
  return categoryUpdatePut(response);
};

/**
 * Delete a category
 * @param {string|number} id - Category ID or slug
 * @returns {Promise} Deletion response
 */
export const deleteCategory = async (slack) => {
  const response = await del(`${process.env.EXTENDED_URL}/category/${slack}`);
  return categoryDeleteDel(response);
};

/**
 * Export categories to file
 * @param {Object} filters - Filters for export
 * @returns {Promise} File data
 */
export const exportCategories = async (filters = {}) => {
  try {
    let queryParams = {};
    if (filters) {
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        queryParams.filters = JSON.stringify({ search_attr: filters.search, store_id: localStorage.getItem('store_id'), download: "xlsx" });
      } else {
        const filtersObj = { store_id: localStorage.getItem('store_id'), download: "xlsx" };
        // if (filters.sort && filters.sort.length > 0) {
        //   filtersObj.sort = filters.sort.map(item => ({
        //     colId: item.field,
        //     sort: item.sort
        //   }));
        // }
        queryParams.filters = JSON.stringify(filtersObj);
      }
    }
    const response = await get(`${process.env.EXTENDED_URL}/report/category`, queryParams);
    return response;
  } catch (error) {
    console.error('Error exporting categories:', error);
    throw error;
  }
};

/**
 * Update category status
 * @param {string} slack - Category slack ID
 * @param {Object} statusData - Status update data
 * @returns {Promise} Status update response
 */
export const updateCategoryStatus = async (slack, statusData) => {
  const response = await put(`${process.env.EXTENDED_URL}/category/${slack}/status`, statusData);
  return categoryStatusPut(response);
};

/**
 * Process category deletion response
 * @param {Object} response - API response
 * @returns {Object} Processed response
 */
const categoryDeleteGet = (response) => {
  try {
    if (response.status === 200 && response.data) {
      return {
        success: true,
        message: response.data.message || 'Category deleted successfully'
      };
    }
    
    return {
      success: false,
      message: response.data?.message || 'Failed to delete category'
    };
  } catch (error) {
    console.error('Error in categoryDeleteGet:', error);
    return {
      success: false,
      message: 'Error deleting category'
    };
  }
};

/**
 * Sync categories with PMS
 * @returns {Promise} Sync response
 */
export const syncWithPms = async (data) => {
  const response = await put(`${process.env.EXTENDED_URL}/category/sync-with-pms`, data);
  return syncWithPmsPost(response);
};
